import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtINR, fmtNum, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

/**
 * Stage-scoped dashboard for outsourced KDC(P) stages.
 * - WIP card at top (click to open per-vendor breakdown)
 * - DC list for this stage (Receive / Print / WhatsApp / Edit / Del)
 * - New / Edit DC form
 * The single-line "batch quick entry" has been retired — DC is the single source of truth.
 */

const STAGE_META = {
  knitting:   { title: "Knitting",   step: "02", partyType: "knitter",   unit: "Kg", tone: "amber" },
  dyeing:     { title: "Dyeing / Bleaching", step: "03", partyType: "dyer", unit: "Kg", tone: "cyan" },
  compacting: { title: "Compacting", step: "04", partyType: "compactor", unit: "Kg", tone: "violet" },
  printing:   { title: "Printing",   step: "4b", partyType: "printer",   unit: "Kg", tone: "rose" },
};

const STATUS_STYLE = {
  pending:  "bg-yellow-100 text-yellow-800 border-yellow-300",
  partial:  "bg-blue-100 text-blue-800 border-blue-300",
  received: "bg-green-100 text-green-800 border-green-300",
};

function stageFromPath(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "";
  return STAGE_META[last] ? last : "knitting";
}

const emptyLine = () => ({ hsn: "", qty_sent: "", rate: "", notes: "" });

export default function BatchesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const navigate = useNavigate();
  const stage = stageFromPath(location.pathname);
  const meta = STAGE_META[stage];

  const [list, setList] = useState([]);
  const [parties, setParties] = useState([]);
  const [wipTotal, setWipTotal] = useState(0);
  const [wipOldest, setWipOldest] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [breakdown, setBreakdown] = useState([]);
  const [company, setCompany] = useState({ company_name: "SLKC", tagline: "Selvalakshmi Knits & Co.", address: "", gst_no: "", phone: "" });
  const [suggestedNumber, setSuggestedNumber] = useState("");

  const [form, setForm] = useState({ dc_number: "", date: today(), party_id: "", notes: "", lines: [emptyLine()] });
  const [openDcId, setOpenDcId] = useState(null);
  const [receiveDraft, setReceiveDraft] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");

  const filteredList = useMemo(() => {
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((dc) => {
      const hay = `${dc.dc_number || ""} ${dc.party_name || ""} ${dc.notes || ""} ${(dc.lines || []).map((l) => l.notes || "").join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [list, q]);

  const load = async () => {
    const [dcRes, dashRes, nextRes] = await Promise.all([
      api.get(`/dcs?stage=${stage}`),
      api.get("/dashboard"),
      api.get("/dcs/next-number").catch(() => ({ data: { next: "" } })),
    ]);
    setList(dcRes.data);
    setWipTotal(dashRes.data?.wip?.[`${stage}_kg`] ?? 0);
    setWipOldest(dashRes.data?.wip?.oldest?.[`${stage}_days`] ?? 0);
    setSuggestedNumber(nextRes.data?.next || "");
  };

  useEffect(() => {
    load();
    setEditingId(null);
    setForm({ dc_number: "", date: today(), party_id: "", notes: "", lines: [emptyLine()] });
    // eslint-disable-next-line
  }, [stage]);

  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const filteredParties = useMemo(
    () => parties.filter((p) => !p.type || p.type === meta.partyType),
    [parties, meta.partyType]
  );

  const openDrawer = async () => {
    try {
      const { data } = await api.get(`/wip/kdc/${stage}`);
      setBreakdown(data);
      setDrawerOpen(true);
    } catch { /* silent */ }
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine  = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  const resetForm = () => {
    setForm({ dc_number: "", date: today(), party_id: "", notes: "", lines: [emptyLine()] });
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!form.party_id) { toast.error("Select party"); return; }
      const lines = form.lines
        .filter((l) => Number(l.qty_sent) > 0)
        .map((l) => ({
          product_id: null,
          hsn: (l.hsn || "").trim(),
          qty_sent: Number(l.qty_sent),
          rate: Number(l.rate || 0),
          notes: l.notes || "",
        }));
      if (lines.length === 0) { toast.error("Add at least one line"); return; }
      const payload = {
        dc_number: form.dc_number || suggestedNumber || `DC-${Date.now()}`,
        date: form.date, stage, party_id: form.party_id, notes: form.notes || "", lines,
      };
      if (editingId) {
        if (!window.confirm("Editing a DC will RESET all received quantities to 0 and clear the linked ledger entry. Continue?")) return;
        await api.put(`/dcs/${editingId}`, payload);
        toast.success("DC updated · received qty reset");
      } else {
        await api.post("/dcs", payload);
        toast.success("DC created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const startEdit = (dc) => {
    setEditingId(dc.id);
    setForm({
      dc_number: dc.dc_number, date: dc.date, party_id: dc.party_id, notes: dc.notes || "",
      lines: (dc.lines || []).map((l) => ({ hsn: l.hsn || "", qty_sent: l.qty_sent, rate: l.rate, notes: l.notes || "" })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleOpen = (dc) => {
    if (openDcId === dc.id) { setOpenDcId(null); return; }
    setOpenDcId(dc.id);
    const d = {};
    (dc.lines || []).forEach((l) => { d[l.id] = String(l.qty_received || 0); });
    setReceiveDraft(d);
  };

  const submitReceive = async (dc) => {
    try {
      const receives = (dc.lines || []).map((l) => ({ line_id: l.id, qty_received: Number(receiveDraft[l.id] || 0) }));
      await api.put(`/dcs/${dc.id}/receive`, { receives });
      toast.success("Receive updated · fabric pool + ledger auto-synced");
      load();
      setOpenDcId(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this DC? Ledger entry will be reversed.")) return;
    await api.delete(`/dcs/${id}`);
    load();
  };

  const whatsappDC = (dc) => {
    const party = parties.find((p) => p.id === dc.party_id);
    const phone = (party?.phone || "").replace(/[^\d]/g, "");
    const linesTxt = (dc.lines || []).map((l, i) => {
      const h = l.hsn ? ` [HSN ${l.hsn}]` : "";
      return `${i + 1}.${h} ${l.qty_sent} ${meta.unit}${l.rate ? ` @ ₹${l.rate}` : ""}${l.notes ? ` (${l.notes})` : ""}`;
    }).join("\n");
    const totalSent = (dc.lines || []).reduce((s, l) => s + Number(l.qty_sent || 0), 0);
    const msg = `*SLKC — Delivery Challan*\nDC #: *${dc.dc_number}*\nDate: ${dc.date}\nStage: ${meta.title} (${meta.unit})\nParty: ${dc.party_name || party?.name || "—"}\n\n${linesTxt || "(no lines)"}\n\nTotal Sent: *${totalSent} ${meta.unit}*\nReceived so far: ${dc.total_received || 0} ${meta.unit}\nStatus: ${(dc.status || "").toUpperCase()}\n\nKindly confirm receipt.\n— SLKC`;
    const encoded = encodeURIComponent(msg);
    const url = phone ? `https://wa.me/${phone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    if (!phone) toast.warning("No phone on file for this party — pick contact manually.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const printDC = (dc) => {
    // Reuse the exact same slip generator as DCPage. Kept inline to keep this page self-contained.
    const rows = (dc.lines || []).map((l, i) => `<tr><td class="c">${i + 1}</td><td>${l.product_label || "—"}</td><td class="c">${l.hsn || "—"}</td><td class="r">${l.qty_sent}</td>${isAdmin ? `<td class="r">${l.rate || 0}</td>` : ""}<td class="r">${l.notes ? String(l.notes).replace(/</g, "&lt;") : ""}</td><td class="r bd">${"".padEnd(8, "\u00A0")}</td></tr>`).join("");
    const totalSent = (dc.lines || []).reduce((s, l) => s + Number(l.qty_sent || 0), 0);
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Challan ${dc.dc_number}</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{font:12px "Helvetica Neue",Arial,sans-serif;color:#111;margin:0}.head{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px solid #111;padding-bottom:10px}.brand{font-weight:900;letter-spacing:.05em;font-size:22px}.brand small{display:block;font-size:10px;letter-spacing:.2em;color:#555;margin-top:4px;font-weight:500}.brand .addr{display:block;font-weight:400;font-size:10px;color:#444;margin-top:4px;letter-spacing:normal;max-width:280px;line-height:1.4}.brand .gst{display:block;font-size:10px;margin-top:4px;font-weight:700;letter-spacing:.1em}.doc{text-align:right}.doc .title{font-size:14px;font-weight:800;letter-spacing:.2em;text-transform:uppercase}.doc .num{font-size:22px;font-weight:900;margin-top:2px}.doc .jw{display:inline-block;margin-top:8px;padding:4px 10px;border:1.5px solid #b91c1c;color:#b91c1c;font-weight:800;letter-spacing:.18em;font-size:10px;text-transform:uppercase}.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px}.parties .box{border:1px solid #ddd;padding:10px 12px}.parties .lab{text-transform:uppercase;letter-spacing:.2em;font-size:8.5px;color:#666;margin-bottom:3px}.parties .val{font-weight:800;font-size:13px}.parties .sub{font-size:10.5px;color:#333;margin-top:3px;line-height:1.35}.meta{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:12px;padding:10px 0;border-top:1px dashed #ccc;border-bottom:1px solid #ddd}.meta .lab{text-transform:uppercase;letter-spacing:.2em;font-size:9px;color:#666;margin-bottom:2px}.meta .val{font-weight:700;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:14px}th{text-align:left;text-transform:uppercase;letter-spacing:.15em;font-size:9px;padding:8px 6px;border-bottom:2px solid #111}td{padding:10px 6px;border-bottom:1px dashed #ccc;vertical-align:top}.r{text-align:right;font-variant-numeric:tabular-nums}.c{text-align:center;width:28px}.bd{border-left:1px dashed #999;min-width:90px}tfoot td{border-top:2px solid #111;font-weight:800}.sign{margin-top:60px;display:grid;grid-template-columns:1fr 1fr;gap:40px}.sign .box{border-top:1px solid #111;padding-top:6px;font-size:10px;text-transform:uppercase;letter-spacing:.2em;color:#555}.note{margin-top:14px;font-size:11px;color:#333;white-space:pre-wrap}.foot{margin-top:30px;font-size:9px;color:#888;text-align:center;letter-spacing:.2em;text-transform:uppercase}</style></head><body><div class="head"><div class="brand">${company.company_name || "SLKC"}<small>${company.tagline || "Selvalakshmi · Knits &amp; Co."}</small>${company.address ? `<span class="addr">${String(company.address).replace(/</g, "&lt;")}</span>` : ""}${company.gst_no ? `<span class="gst">GSTIN: ${company.gst_no}</span>` : ""}${company.phone ? `<span class="gst" style="font-weight:400">Ph: ${company.phone}</span>` : ""}</div><div class="doc"><div class="title">Delivery Challan</div><div class="num">${dc.dc_number}</div><div class="jw">For Job Work Only · Not for Sale</div></div></div><div class="parties"><div class="box"><div class="lab">From (Consignor)</div><div class="val">${company.company_name || "SLKC"}</div>${company.address ? `<div class="sub">${String(company.address).replace(/</g, "&lt;")}</div>` : ""}${company.gst_no ? `<div class="sub"><b>GSTIN:</b> ${company.gst_no}</div>` : ""}${company.phone ? `<div class="sub"><b>Ph:</b> ${company.phone}</div>` : ""}</div><div class="box"><div class="lab">To (Consignee · Vendor)</div><div class="val">${dc.party_name || "—"}</div>${dc.party_address ? `<div class="sub">${String(dc.party_address).replace(/</g, "&lt;")}</div>` : ""}${dc.party_gst_no ? `<div class="sub"><b>GSTIN:</b> ${dc.party_gst_no}</div>` : `<div class="sub" style="color:#888">GSTIN: —</div>`}${dc.party_phone ? `<div class="sub"><b>Ph:</b> ${dc.party_phone}</div>` : ""}</div></div><div class="meta"><div><div class="lab">Date</div><div class="val">${dc.date}</div></div><div><div class="lab">Stage</div><div class="val">${meta.title} · ${meta.unit}</div></div><div><div class="lab">Purpose</div><div class="val">Job Work — Return Expected</div></div><div><div class="lab">DC Status</div><div class="val">${(dc.status || "").toUpperCase()}</div></div></div><table><thead><tr><th>#</th><th>Item / Product</th><th class="c">HSN</th><th class="r">Sent ${meta.unit}</th>${isAdmin ? `<th class="r">Rate ₹</th>` : ""}<th class="r">Notes</th><th class="r">Received ${meta.unit}</th></tr></thead><tbody>${rows || `<tr><td colspan="${isAdmin ? 7 : 6}" style="text-align:center;color:#888;padding:20px">No lines</td></tr>`}</tbody><tfoot><tr><td colspan="${isAdmin ? 3 : 3}">TOTAL</td><td class="r">${totalSent}</td><td colspan="${isAdmin ? 2 : 1}"></td><td class="r bd"></td></tr></tfoot></table>${dc.notes ? `<div class="note"><b>Notes:</b> ${String(dc.notes).replace(/</g, "&lt;")}</div>` : ""}<div class="note" style="margin-top:18px;padding:8px 10px;background:#fff7ed;border:1px dashed #c2410c;color:#9a3412;font-weight:600">This challan is issued <b>strictly for job work</b> under Rule 55 of CGST Rules. Goods are <b>not for sale</b> and must be returned after the specified processing.</div><div class="sign"><div class="box">Issued by (SLKC)</div><div class="box">Received by (Vendor · Signature &amp; Date)</div></div><div class="foot">Computer-generated · SLKC Manufacturing OS</div><script>window.onload=()=>{window.print()}</script></body></html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocked. Allow pop-ups to print."); return; }
    w.document.write(html); w.document.close();
  };

  const alarmingWip = wipTotal > 0 && wipOldest >= 7;

  return (
    <div>
      <PageHeader
        label={`Stage ${meta.step}`}
        title={`${meta.title} Dashboard`}
        subtitle={`Outsourced ${meta.title} · ${meta.unit} · DC-based tracking · Fabric pool auto-updated on receive`}
        right={<button onClick={() => navigate("/dcs")} className="label-caps hover:text-foreground" data-testid={`stage-${stage}-all-dcs`}>All DCs ›</button>}
      />

      <div className="px-4 md:px-8 pt-4">
        <div
          className={`bg-white border-2 ${wipTotal > 0 ? "border-foreground" : "border-border"} p-4 flex items-center gap-6 cursor-pointer hover:bg-secondary/40 transition-colors`}
          data-testid={`stage-${stage}-wip`}
          onClick={openDrawer}
        >
          <div className="flex-1">
            <div className="label-caps text-muted-foreground">{meta.title} WIP · Out & Pending Return</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display font-black text-4xl leading-none tabular tracking-tight">{fmtNum(wipTotal, 1)}</span>
              <span className="label-caps px-1.5 py-0.5 bg-secondary">Kg</span>
              {wipTotal > 0 && (
                <span className={`ml-2 label-caps px-1.5 py-0.5 border ${alarmingWip ? "bg-destructive/10 text-destructive border-destructive/40" : "border-border text-muted-foreground"}`}>
                  oldest {wipOldest}d
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] font-mono text-muted-foreground">
              {wipTotal > 0 ? "tap to see per-vendor breakdown" : "no pending work — all clear"}
            </div>
          </div>
          {wipTotal > 0 && <span className="label-caps text-muted-foreground">tap ›</span>}
        </div>
      </div>

      <div className="p-4 md:p-8 grid grid-cols-1 xl:grid-cols-5 gap-3 md:gap-4">
        <Card title={editingId ? `Edit DC · ${meta.title}` : `New DC · ${meta.title}`} className="xl:col-span-2">
          <form onSubmit={submit} className="space-y-3" data-testid={`stage-${stage}-form`}>
            {editingId && (
              <div className="bg-amber-50 border border-amber-300 p-2 text-[11px] font-mono">
                ⚠ Editing this DC will RESET all received quantities and clear its ledger entry.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Field label="DC #">
                <div className="flex gap-1">
                  <Input placeholder={suggestedNumber || "Auto if blank"} value={form.dc_number} onChange={(e) => setForm({ ...form, dc_number: e.target.value })} data-testid={`stage-${stage}-dc-number`} />
                  {!editingId && suggestedNumber && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, dc_number: suggestedNumber })}
                      title={`Use ${suggestedNumber}`}
                      className="h-9 px-2 border border-border bg-white font-mono text-[10px] tracking-[0.1em] uppercase font-bold hover:bg-secondary whitespace-nowrap"
                    >
                      Use {suggestedNumber.split("/").pop()}
                    </button>
                  )}
                </div>
              </Field>
              <Field label="Date"><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            </div>
            <Field label={`Party (${meta.partyType})`}>
              <Select required value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })} data-testid={`stage-${stage}-party`}>
                <option value="">Select…</option>
                {filteredParties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <div className="border border-border p-3 bg-secondary space-y-2">
              <div className="flex items-center justify-between">
                <span className="label-caps">Lines · {meta.unit}</span>
                <button type="button" onClick={addLine} className="label-caps hover:text-foreground">+ Add line</button>
              </div>
              {form.lines.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-start bg-white p-2 border border-border">
                  <div className="col-span-3"><Input type="number" step="0.01" placeholder={`Sent ${meta.unit}`} required value={ln.qty_sent} onChange={(e) => setLine(i, { qty_sent: e.target.value })} /></div>
                  {isAdmin && <div className="col-span-3"><Input type="number" step="0.01" placeholder="Rate ₹" value={ln.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></div>}
                  <div className="col-span-2"><Input placeholder="HSN" value={ln.hsn} onChange={(e) => setLine(i, { hsn: e.target.value.replace(/[^\d]/g, "").slice(0, 8) })} /></div>
                  <div className={isAdmin ? "col-span-3" : "col-span-6"}><Input placeholder="Notes" value={ln.notes} onChange={(e) => setLine(i, { notes: e.target.value })} /></div>
                  <div className="col-span-1 text-right">
                    {form.lines.length > 1 && <button type="button" onClick={() => rmLine(i)} className="text-destructive font-mono text-xs h-9 px-1">×</button>}
                  </div>
                </div>
              ))}
            </div>
            <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <div className="flex gap-2">
              <Btn>{editingId ? "Update DC" : "Save DC"}</Btn>
              {editingId && (
                <button type="button" onClick={resetForm} className="h-9 px-4 border border-border font-mono text-[11px] tracking-[0.15em] uppercase font-bold">Cancel</button>
              )}
            </div>
          </form>
        </Card>

        <Card
          title={`${meta.title} DCs · ${filteredList.length}${q ? ` (filtered from ${list.length})` : ""}`}
          right={
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendor / DC# / notes…"
              className="h-8 px-2 bg-white border border-border font-mono text-xs w-56"
              data-testid={`stage-${stage}-search`}
            />
          }
          className="xl:col-span-3"
        >
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th>DC #</th><th>Date</th><th>Party</th>
                  <th className="text-right">Sent {meta.unit}</th>
                  <th className="text-right">Recv {meta.unit}</th>
                  {isAdmin && <th className="text-right">Bill</th>}
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((dc) => (
                  <React.Fragment key={dc.id}>
                    <tr>
                      <td className="font-mono font-semibold">{dc.dc_number}</td>
                      <td className="font-mono">{dc.date}</td>
                      <td>{dc.party_name}</td>
                      <td className="tabular text-right">{dc.total_sent}</td>
                      <td className="tabular text-right font-bold">{dc.total_received}</td>
                      {isAdmin && <td className="tabular text-right">{fmtINR(dc.total_cost || 0)}</td>}
                      <td><span className={`inline-block label-caps px-2 py-0.5 border ${STATUS_STYLE[dc.status]}`}>{dc.status}</span></td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => toggleOpen(dc)} data-testid={`stage-dc-open-${dc.id}`} className="label-caps hover:text-foreground mr-3">{openDcId === dc.id ? "Close" : "Receive"}</button>
                        <button onClick={() => printDC(dc)} className="label-caps hover:text-foreground mr-3">Print</button>
                        <button onClick={() => whatsappDC(dc)} className="label-caps text-green-700 hover:text-green-900 mr-3">WhatsApp</button>
                        {isAdmin && (
                          <>
                            <button onClick={() => startEdit(dc)} data-testid={`stage-dc-edit-${dc.id}`} className="label-caps hover:text-foreground mr-3">Edit</button>
                            <button onClick={() => del(dc.id)} className="label-caps hover:text-destructive">Del</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {openDcId === dc.id && (
                      <tr>
                        <td colSpan={8} className="bg-secondary p-0">
                          <div className="p-3">
                            <div className="label-caps mb-2">Partial Receive · enter exact received {meta.unit} per line</div>
                            <table className="table-dense w-full bg-white">
                              <thead><tr><th className="text-right">Sent {meta.unit}</th>{isAdmin && <th className="text-right">Rate ₹</th>}<th className="text-right">Received {meta.unit}</th>{isAdmin && <th className="text-right">Bill</th>}<th>Status</th><th>Notes</th></tr></thead>
                              <tbody>
                                {(dc.lines || []).map((l) => {
                                  const draftQty = Number(receiveDraft[l.id] || 0);
                                  const s = draftQty <= 0 ? "pending" : draftQty < Number(l.qty_sent) ? "partial" : "received";
                                  return (
                                    <tr key={l.id}>
                                      <td className="tabular text-right">{l.qty_sent}</td>
                                      {isAdmin && <td className="tabular text-right">{l.rate}</td>}
                                      <td className="text-right">
                                        <input type="number" step="0.01" min="0" max={l.qty_sent}
                                          value={receiveDraft[l.id] ?? ""}
                                          onChange={(e) => setReceiveDraft({ ...receiveDraft, [l.id]: e.target.value })}
                                          className="h-8 px-2 border border-border bg-white text-right font-mono text-xs w-24" />
                                      </td>
                                      {isAdmin && <td className="tabular text-right">{fmtINR(draftQty * Number(l.rate || 0))}</td>}
                                      <td><span className={`inline-block label-caps px-2 py-0.5 border ${STATUS_STYLE[s]}`}>{s}</span></td>
                                      <td className="text-muted-foreground text-xs">{l.notes}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div className="mt-3 flex gap-2">
                              <button type="button" onClick={() => submitReceive(dc)} className="h-9 px-4 bg-foreground text-background font-mono text-[11px] tracking-[0.15em] uppercase font-bold">Save Receive</button>
                              <button type="button" onClick={() => setOpenDcId(null)} className="h-9 px-4 border border-border font-mono text-[11px] tracking-[0.15em] uppercase font-bold">Cancel</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filteredList.length === 0 && <tr><td colSpan={8}><Empty text={q ? "No DCs match your search" : `No ${meta.title} DCs yet — create one on the left`} /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid={`stage-${stage}-drawer`}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl border-l border-border">
            <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-start justify-between">
              <div>
                <div className="label-caps text-muted-foreground">Vendor WIP · Breakdown</div>
                <div className="font-display text-2xl font-black mt-1">{meta.title} · {fmtNum(wipTotal, 1)} Kg</div>
                <div className="text-xs text-muted-foreground mt-1">Grouped by {meta.partyType} · oldest first</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="label-caps hover:text-destructive">Close ✕</button>
            </div>
            <div className="p-5 space-y-4">
              {breakdown.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">All {meta.partyType}s clear — no pending Kg.</div>}
              {breakdown.map((p) => {
                const alarming = p.oldest_days >= 7;
                return (
                  <div key={p.party_id} className="border border-border bg-white">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <div>
                        <div className="font-black text-lg">{p.party_name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground mt-0.5">Batch {p.batch_kg} · DC {p.dc_kg} Kg</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl font-black tabular">{p.total_kg}</div>
                        <div className={`label-caps mt-1 inline-block px-1.5 py-0.5 border ${alarming ? "bg-destructive/10 text-destructive border-destructive/40" : "border-border text-muted-foreground"}`}>oldest {p.oldest_days}d</div>
                      </div>
                    </div>
                    <table className="table-dense w-full text-xs">
                      <thead><tr><th>Source</th><th>Date</th><th className="text-right">Kg</th></tr></thead>
                      <tbody>
                        {p.sources.map((s, i) => (
                          <tr key={s.id + "-" + i}>
                            <td>
                              <span className={`label-caps px-1.5 py-0.5 ${s.type === "dc" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{s.type === "dc" ? "DC" : "Batch"}</span>
                              <span className="ml-2 font-mono text-[11px]">{s.ref}</span>
                            </td>
                            <td className="font-mono">{s.date}</td>
                            <td className="tabular text-right font-bold">{s.kg}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
